import { JobCompletion } from "../components/JobCompletion";
import { JobBooking } from "../components/JobBooking";
import { JobLocation } from "../components/JobLocation";
import { ClientDiary } from "../components/ClientDiary";
/**
 * The job card.
 *
 * The screen the whole product argues for: quoted against actual, in the hand
 * of the person spending the hours, while they are still on site. If a guard
 * is up it is the first thing on the page — a warning below the fold is a
 * warning nobody read.
 *
 * It reads from the device, so it opens in a comms room. The margin shown is
 * recomputed from what the phone is holding rather than read off the row, so a
 * part added two minutes ago is in it — see `repo.job`. Arriving, adding a
 * part and booking time all go through `actions`, which tries the server and
 * falls back to the queue.
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import * as actions from "../lib/actions";
import { ApiError } from "../lib/api";
import { hours, marginTone, money, percent, qty, time, titleCase } from "../lib/format";
import { useLocal } from "../lib/local";
import * as repo from "../lib/repo";
import { InstallRegister } from "../components/InstallRegister";
import { JobWriteUp } from "../components/JobWriteUp";
import { Card, Chip, DataTable, Loading, PageHeader, type Tone } from "../components/ui";
import type { CostingOut, Guard } from "../lib/types";

const GUARD_COPY: Record<Exclude<Guard, "none">, { title: string; tone: Tone }> = {
  unapproved_variation: {
    title: "A variation is waiting on approval",
    tone: "warn",
  },
  over_quoted_hours: {
    title: "Past the quoted hours",
    tone: "warn",
  },
  below_break_even: {
    title: "Below break-even",
    tone: "crit",
  },
};

export default function JobDetail() {
  const { id = "" } = useParams();
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Local first; a job this device doesn't carry is fetched and kept.
  const { data: job, isPending, reload } = useLocal(async () => (await repo.job(id)) ?? repo.fetchJob(id), [id]);

  async function arrive() {
    setBusy(true);
    setFormError(null);
    try {
      const { queued, message } = await actions.arrive(id);
      setNotice(queued ? (message ?? null) : null);
      reload();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Couldn't record arrival.");
    } finally {
      setBusy(false);
    }
  }

  if (isPending) return <Loading what="Opening the job" />;

  if (!job) {
    // Not on this device. Said plainly rather than as an error, because the
    // usual cause is a job belonging to another tech — the working set carries
    // this technician's own work on purpose (D16).
    return (
      <div className="page">
        <div className="notbuilt">
          <h2>Not on this device</h2>
          <p>
            This job isn't in what your phone is carrying. If you're back in
            range, sync and try again — otherwise it belongs to someone else's
            run.
          </p>
        </div>
      </div>
    );
  }

  const costing = job.costing;
  const guard = costing?.guard ?? "none";

  return (
    <div className="page">
      <PageHeader eyebrow={`${job.number} · ${titleCase(job.type)}`} title={job.title} actions={<Link className="btn" to={`/customers/${job.customer_id}`}>Customer & bookings</Link>}>
        {job.description}
      </PageHeader>

      {guard !== "none" && costing && <GuardBanner guard={guard} costing={costing} />}

      {formError && (
        <p className="notice notice-crit" role="alert">
          {formError}
        </p>
      )}

      {notice && (
        <p className="notice notice-info" role="status">
          {notice}
        </p>
      )}

      <div className="split">
        <div className="split-main">
          <JobLocation jobId={id} siteId={job.site_id} />
          <ClientDiary jobId={id} />
          <Card
            title="Parts and labour"
            subtitle={`${job.lines.length} line${job.lines.length === 1 ? "" : "s"}`}
            padded={false}
          >
            {job.lines.length === 0 ? (
              <p className="state">Nothing booked yet — the tech hasn't arrived.</p>
            ) : (
              <DataTable
                columns={[
                  { key: "d", label: "Line" },
                  { key: "q", label: "Qty" },
                  { key: "c", label: "Cost", align: "right", hideOnPhone: true },
                  { key: "s", label: "Charge", align: "right" },
                ]}
              >
                {job.lines.map((line) => (
                  <tr key={line.id}>
                    <td>
                      {line.description}
                      {line.under_warranty && (
                        <>
                          {" "}
                          <Chip tone="warn">warranty</Chip>
                        </>
                      )}
                      {line.serial && <div className="mono tiny muted">SN {line.serial}</div>}
                    </td>
                    <td className="mono">{qty(line.qty)}</td>
                    <td className="right mono muted phone-hide">{money(line.unit_cost_cents)}</td>
                    <td className="right mono">
                      {line.under_warranty || !line.billable ? "—" : money(line.unit_sell_cents)}
                    </td>
                  </tr>
                ))}
              </DataTable>
            )}
          </Card>

          <InstallRegister jobId={job.id} />

          <JobWriteUp key={`${job.id}-${job.updated_at}`} job={job} onSaved={reload} />

          <JobCompletion key={id} jobId={id} onSaved={reload} />

          <Card title="Status">
            <dl className="kv">
              <dt>Status</dt>
              <dd>
                <Chip tone="info">{titleCase(job.status)}</Chip>
              </dd>
              <dt>Scheduled</dt>
              <dd className="mono">
                <JobBooking id={id} start={job.scheduled_start} end={job.scheduled_end} />
              </dd>
              {job.sla_due_at && (
                <>
                  <dt>SLA due</dt>
                  <dd className="mono">{time(job.sla_due_at)}</dd>
                </>
              )}
            </dl>
            <div className="btn-row">
              <button
                className="btn"
                type="button"
                disabled={busy || ["on_site", "completed", "invoiced", "cancelled"].includes(job.status)}
                onClick={() => void arrive()}
              >
                {job.status === "on_site" ? "On site" : busy ? "Recording…" : "I've arrived"}
              </button>
              <Link className="btn" to={`/invoices?job=${job.id}`}>
                Invoice this job
              </Link>
            </div>
          </Card>
        </div>

        <aside className="split-side">
          {costing && <MarginCard costing={costing} quoted={job.quoted_cents} />}
        </aside>
      </div>
    </div>
  );
}

function GuardBanner({ guard, costing }: { guard: Exclude<Guard, "none">; costing: CostingOut }) {
  const copy = GUARD_COPY[guard];
  return (
    <div className={`guard guard-${copy.tone}`} role="alert">
      <b>{copy.title}</b>
      <ul>
        {costing.warnings.map((w) => (
          <li key={w}>{w}</li>
        ))}
      </ul>
    </div>
  );
}

function MarginCard({ costing, quoted }: { costing: CostingOut; quoted: number }) {
  const tone = marginTone(costing.margin_bp);
  const fixedPrice = quoted > 0;

  return (
    <Card title="Live margin" subtitle={fixedPrice ? "Fixed price" : "Time & materials"}>
      <div className="bar" style={{ marginBottom: 12 }}>
        <i
          className={`bar-fill bar-${tone}`}
          style={{ width: `${Math.max(0, Math.min(100, costing.margin_bp / 100))}%` }}
        />
      </div>

      <dl className="kv kv-money">
        <dt>{fixedPrice ? "Quoted" : "Billable"}</dt>
        <dd className="mono">{money(costing.revenue_cents)}</dd>
        <dt>Cost so far</dt>
        <dd className="mono">{money(costing.cost_cents)}</dd>
        <dt className="total">Margin</dt>
        <dd className={`mono total text-${tone}`}>
          {money(costing.margin_cents)} · {percent(costing.margin_bp)}
        </dd>
      </dl>

      <dl className="kv" style={{ marginTop: 12 }}>
        <dt>Hours</dt>
        <dd className="mono">
          {hours(costing.hours_actual)}
          {/* Against the ceiling, not the bare quote: an approved variation
              lifted it, and nagging about hours that were signed off is how a
              warning stops being read. */}
          {costing.hours_ceiling !== null && ` of ${hours(costing.hours_ceiling)}`}
        </dd>
        {costing.headroom_cents !== null && (
          <>
            <dt>Headroom</dt>
            <dd className={`mono ${costing.headroom_cents < 0 ? "text-crit" : ""}`}>
              {money(costing.headroom_cents)}
            </dd>
          </>
        )}
      </dl>

      {fixedPrice && (
        <p className="tiny muted" style={{ marginTop: 10 }}>
          On a fixed-price job the lines track cost — the quote is what bills. An
          absorbed part eats margin rather than inflating the invoice.
        </p>
      )}
    </Card>
  );
}
